import React, { useEffect, useState } from "react";
import { addMonths, format, getDate, getDay, subMonths } from "date-fns";
import "./K003.scss";
import { FLG_HOLIDAY, FLG_CLOSEDAY, FLG_NOT_TARGET_MONTH, FLG_REGULAR_HOLIDAY, FLG_WORKING_DAY_AT_TARGET_MONTH } from "../../models/schedule.model";
import { getCalendarArray } from "../../helper/schedule.helper";
import { useLocation } from "react-router";
import { getState } from "../../helper/page.helper";
import { useLoadingIndicator } from "../../hooks/LoadingIndicator.hooks";
import Title from "../../components/Title/Title";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import { MdNavigateNext } from "react-icons/md";
import { MdNavigateBefore } from "react-icons/md";
import Label from "../../components/Label/Label";
import CustomButton, { CB_ALIGN, CB_CLASS } from "../../components/Button/CustomButton";
import ConfirmModal from "../../components/ConfirmModal";
import Panel, { PANEL_COLOR, PANEL_BG_COLOR } from "../../components/Panel/Panel";
import { fetchHolidaySettingTheMonth, putK003 } from "../../services/api.service";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors/auth.selector";
import { K003Schema, K003PutRequestSchema } from "../../generated";
import { useAuth } from "../../helper/auth.helper";
import { notify, notifyError } from "../../helper/settings.helper";
import { FLG_SATURDAY, FLG_SUNDAY } from "../../constants";
import FacilityName from "../../components/FacilityName/FacilityName";
import CustomModal from "../../components/CustomModal/CustomModal";
import { IoIosHelpCircleOutline } from "react-icons/io";
import DescribeCalendar from "./DescribeCalendar";

interface K003Location {
  facility_Id: number,
  target_date: string,
  holiday: string,
}

const K003: React.VFC = () => {
  const location = useLocation<K003Location>();
  const { checkLoggedIn } = useAuth();
  const authState = useSelector(authSelector);
  const state = getState(location);
  const fcId = state.facility_id;
  const [targetDate, setTargetDate] = useState(new Date(state.target_date));
  const holiday = String(state.holiday);
  const calendar = getCalendarArray(targetDate);
  const holidayArray = (holiday.slice(-1) + holiday.slice(0, -1)).split("");
  const [isLoading, , startLoading, stopLoading] = useLoadingIndicator();
  const [showRegister, setShowRegister] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changeDateList, setChangeDateList] = useState<string[]>([]);
  const [changeCloseDateList, setChangeCloseDateList] = useState<string[]>([]);
  const [registeredDate, setRegisteredDate] = useState<K003Schema[]>([]);
  const [isNextMonth, setIsNextMonth] = useState<boolean>();
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const SELECT_MODE = {
    HOLIDAY: 1,
    CLOSEDAY: 2,
  };
  const SELECT_MESSAGE = {
    HOLIDAY: "祝日を選択してください。",
    CLOSEDAY: "施設休業日を選択してください。",
  };
  const [isAddingEmptySlots, setIsAddingEmptySlots] = useState<boolean>(false);
  const [selectMode, setSelectMode] = useState<number>(SELECT_MODE.HOLIDAY);
  const [message, setMessage] = useState<string>(SELECT_MESSAGE.HOLIDAY);
  const [getData, setGetData] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      startLoading();
      try {
        const res = await fetchHolidaySettingTheMonth(authState, fcId, targetDate.getFullYear(), targetDate.getMonth() + 1);
        if (isMounted) {
          setRegisteredDate(res.data);
          setChangeDateList([]);
          setChangeCloseDateList([]);
        }
      } catch (err) {
        if (isMounted) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (isMounted) {
          stopLoading();
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    }
  }, [authState, checkLoggedIn, fcId, targetDate, startLoading, stopLoading]);

  const selectPanelColor = (date: Date) => {
    let panelColor = PANEL_COLOR.NONE
    const foundRegisteredDate = registeredDate.find((oneDay) => oneDay.date == format(date, "yyyy-MM-dd") && oneDay.status != FLG_CLOSEDAY);
    const foundRegisteredCloseDate = registeredDate.find((oneDay) => oneDay.date == format(date, "yyyy-MM-dd") && oneDay.status == FLG_CLOSEDAY);
    const foundChangeDate = changeDateList.find((oneDay) => oneDay == format(date, "yyyy-MM-dd"))
    const foundChangeCloseDate = changeCloseDateList.find((oneDay) => oneDay == format(date, "yyyy-MM-dd"))
    if (foundRegisteredCloseDate != undefined) panelColor = PANEL_COLOR.GRAY;
    if (SELECT_MODE.HOLIDAY == selectMode) {
      if (foundRegisteredDate != undefined) {
        if (foundRegisteredDate.status == FLG_HOLIDAY) panelColor = PANEL_COLOR.RED;
      } else {
        if (date.getDay() == FLG_SATURDAY || date.getDay() == FLG_SUNDAY) panelColor = PANEL_COLOR.RED;
      }
    }

    if (foundChangeCloseDate != undefined) {
      panelColor = panelColor == PANEL_COLOR.NONE ? PANEL_COLOR.GRAY : PANEL_COLOR.NONE;
      if (SELECT_MODE.HOLIDAY == selectMode && panelColor == PANEL_COLOR.NONE) {
        if (date.getDay() == FLG_SATURDAY || date.getDay() == FLG_SUNDAY) panelColor = PANEL_COLOR.RED;
      }
    }
    if (SELECT_MODE.HOLIDAY == selectMode && foundChangeDate != undefined) {
      panelColor = panelColor ==  PANEL_COLOR.NONE ? PANEL_COLOR.RED : PANEL_COLOR.NONE;
    }

    if (panelColor == PANEL_COLOR.NONE) {
      panelColor = [panelColor, (SELECT_MODE.HOLIDAY == selectMode ? PANEL_BG_COLOR.RED : PANEL_BG_COLOR.GRAY)].join(" ");
    }
    return panelColor
  };

  const handleClick = (date: Date) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    if (SELECT_MODE.HOLIDAY == selectMode) {
      setChangeDateList(prevList => {
        if (prevList.includes(formattedDate)) {
          return prevList.filter(item => item !== formattedDate);
        } else {
          return [...prevList, formattedDate];
        }
      });
    } else {
      setChangeCloseDateList(prevList => {
        if (prevList.includes(formattedDate)) {
          return prevList.filter(item => item !== formattedDate);
        } else {
          return [...prevList, formattedDate];
        }
      });
    }
  };

  const judgeDateStatus = (date: Date) => {
    let dateStatus = FLG_WORKING_DAY_AT_TARGET_MONTH
    if (date.getMonth() + 1 != targetDate.getMonth() + 1) {
      dateStatus = FLG_NOT_TARGET_MONTH;
    } else if (holidayArray[date.getDay()] == FLG_HOLIDAY) {
      dateStatus = FLG_REGULAR_HOLIDAY;
    }
    if (SELECT_MODE.HOLIDAY == selectMode) {
      const foundRegisteredCloseDate = registeredDate.find((oneDay) => oneDay.date == format(date, "yyyy-MM-dd") && oneDay.status == FLG_CLOSEDAY);
      const foundChangeCloseDate = changeCloseDateList.find((oneDay) => oneDay == format(date, "yyyy-MM-dd"));
      if ((foundRegisteredCloseDate != undefined && foundChangeCloseDate == undefined)
      || (foundRegisteredCloseDate == undefined && foundChangeCloseDate != undefined)) {
        dateStatus = FLG_REGULAR_HOLIDAY;
      }
    } 
    return dateStatus
  };

  const clickRegister = async () => {
    let requestBody: K003PutRequestSchema = {
      dates:changeDateList,
      close_dates:changeCloseDateList
    };
    console.log(requestBody);
    startLoading();
    try {
      await putK003(authState, fcId, targetDate.getFullYear(), targetDate.getMonth() + 1, requestBody);
      setShowRegister(false);
      notify("祝日・休業日設定を更新しました。");
      setChangeDateList([]);
      setChangeCloseDateList([]);
      
      // データ更新後に最新データを再取得
      const res = await fetchHolidaySettingTheMonth(authState, fcId, targetDate.getFullYear(), targetDate.getMonth() + 1);
      setRegisteredDate(res.data);
    } catch (err) {
      checkLoggedIn(err);
      setShowRegister(false);
      notifyError("登録に失敗しました。");
    } finally {
      stopLoading();
    }
  };

  const changeCurrentMonth = (pushMoveToNextMonth: boolean) => {
    if (changeDateList.length > 0) {
      setIsNextMonth(pushMoveToNextMonth);
      setShowConfirm(true);
    } else {
      pushMoveToNextMonth ? setTargetDate((current) => addMonths(current, 1)) : setTargetDate((current) => subMonths(current, 1));
    }
  };

  const acceptChangeMonth = () => {
    setShowConfirm(false);
    setChangeDateList([]);
    setChangeCloseDateList([]);
    isNextMonth ? setTargetDate((current) => addMonths(current, 1)) : setTargetDate((current) => subMonths(current, 1));
  };

  const changeSelectMode = () => {
    if (SELECT_MODE.HOLIDAY == selectMode) {
      setSelectMode(SELECT_MODE.CLOSEDAY);
      setMessage(SELECT_MESSAGE.CLOSEDAY);
    } else {
      setSelectMode(SELECT_MODE.HOLIDAY);
      setMessage(SELECT_MESSAGE.HOLIDAY);
    }
  };

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <Title title="祝日・休業日設定" />
      <FacilityName fcId={fcId} authState={authState}/>
      <div className="plate">
        <Label value={message} />
        <div className="on_same_row justify flex-wrap">
          <div className="on_same_row">
            <MdNavigateBefore size="40px" className="move_calendar" onClick={() => {
              changeCurrentMonth(false);
            }}/>
            <MdNavigateNext size="40px" className="move_calendar" onClick={() => {
              changeCurrentMonth(true);}
            }/>
            <h1 className="title-year-month">{format(targetDate, "y年M月")}</h1>
          <IoIosHelpCircleOutline size="40px" className="icon_pointer mx-2" onClick={() => setShowHelp((prevShow) => !prevShow)}/>
          <CustomModal show={showHelp} setShow={setShowHelp} size="lg"
            headerContents={
              <div className="help_header">色別設定</div>
            }
            contents={
              <DescribeCalendar />
            }
          />
          </div>
          <div className="on_same_row justify">
            <CustomButton label="休業日設定" class={isAddingEmptySlots ? [CB_CLASS.BLUE] : [CB_CLASS.LIGHT_GRAY]} align={CB_ALIGN.CENTER} onClick={() => {setIsAddingEmptySlots(!isAddingEmptySlots); changeSelectMode();}}/>
          </div>
        </div>
          <div className="calender-main">
            <table className="table-calender">
              <thead>
                <tr className="headers">
                  <th className="header_title">日</th>
                  <th className="header_title">月</th>
                  <th className="header_title">火</th>
                  <th className="header_title">水</th>
                  <th className="header_title">木</th>
                  <th className="header_title">金</th>
                  <th className="header_title">土</th>
                </tr>
              </thead>
              <tbody>
                {calendar.map((weekRow, rowNum) => (
                  <tr key={rowNum}>
                    {weekRow.map((date) => {

                      return (
                        <td key={getDay(date)} className="item-day">
                          {judgeDateStatus(date) != FLG_WORKING_DAY_AT_TARGET_MONTH ?
                            <Panel
                              label={getDate(date)}
                              disabled={true}
                              color={judgeDateStatus(date) == FLG_NOT_TARGET_MONTH ? PANEL_COLOR.MORE_GRAY : PANEL_COLOR.GRAY}
                            />
                          :
                            <Panel
                              label={getDate(date)}
                              disabled={false}
                              color={selectPanelColor(date)}
                              onClick={() => handleClick(date)}
                            />
                          }
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        <CustomButton label={"登録"} class={[CB_CLASS.RED, "my-3"]} align={CB_ALIGN.CENTER} onClick={() => setShowRegister(true)} />
        <ConfirmModal target="登録" show={showRegister} setShow={setShowRegister} func={clickRegister}
          confirmText={"登録してよろしいでしょうか。"}
        />
        <ConfirmModal target="月度の移動" show={showConfirm} setShow={setShowConfirm} func={acceptChangeMonth}
          confirmText={"当月の登録未完了です。変更を破棄して月度を移動してよろしいでしょうか。"}
        />
      </div>
      
    </>
  );
};

export default K003;
